import debounce from 'lodash/debounce';
import isArray from 'lodash/isArray';
import React, { FC, useEffect, useState, useMemo, useCallback } from 'react';

import {
  apiGetFeatureFlagList,
  apiGetFeatureFlagListWithSearch,
  apiSaveFeatureFlagList,
} from 'src/api/featureFlags';
import { UiButton } from 'src/components/UI/Button';
import { UiCleanCheckbox } from 'src/components/UI/checkbox/CleanCheckbox';
import { UiCleanInput } from 'src/components/UI/inputs/CleanInput';
import { UiToast } from 'src/components/UI/toasts';
import { UiTabs, UiTabsPropsType } from 'src/components/UI/Tabs';
import { UiTable } from 'src/components/UI/table/UiTable';
import { UiTableColumnItemType } from 'src/components/UI/table/UiTable.helpers';
import { FeatureToggleScopeEnum, FeatureFlagItemType } from 'src/models/featureFlags';
import { FlaggedRoutes, routes } from 'src/routes';
import { CDate } from 'src/utils/CDate';
import { useOneRequest } from 'src/utils/hooks/useOneRequest';
import { useStateForm } from 'src/utils/stateForm';
import 'src/styles/pages/featureFlags.scss';
import styles from './styles.module.scss';

const SCOPE_LABELS: Record<FeatureToggleScopeEnum, string> = {
  [FeatureToggleScopeEnum.Global]: 'Global',
  [FeatureToggleScopeEnum.Partner]: 'Partner',
  [FeatureToggleScopeEnum.Organization]: 'Organization',
  [FeatureToggleScopeEnum.Campaign]: 'Campaign',
  [FeatureToggleScopeEnum.User]: 'User',
};

const SCOPES_WITH_SEARCH = new Set([
  FeatureToggleScopeEnum.Partner,
  FeatureToggleScopeEnum.Organization,
  FeatureToggleScopeEnum.Campaign,
  FeatureToggleScopeEnum.User,
]);

const SEARCH_PLACEHOLDERS: Partial<Record<FeatureToggleScopeEnum, string>> = {
  [FeatureToggleScopeEnum.Partner]: 'Search by partner ID...',
  [FeatureToggleScopeEnum.Organization]: 'Search by organization ID...',
  [FeatureToggleScopeEnum.Campaign]: 'Search by campaign ID...',
  [FeatureToggleScopeEnum.User]: 'Search by user ID...',
};

const toFlagValues = (items: FeatureFlagItemType[]) =>
  Object.fromEntries(items.map(({ id, isEnabled }) => [id, isEnabled]));

const noop = Function.prototype as () => void;

const DATE_FORMAT = 'M/dd/yyyy HH:mm:ss';

/** Content for a single scope tab — self-contained with its own data fetching */
const ScopeContent: FC<{ scope: FeatureToggleScopeEnum }> = ({ scope }) => {
  const [flagItems, setFlagItems] = useState<FeatureFlagItemType[]>([]);
  const [activeEntityId, setActiveEntityId] = useState<number | undefined>();

  const formProps = useStateForm<{ flags: Record<string, boolean>; search: string }>({ mode: 'onBlur' });
  const { setValue, onSubmit } = formProps;

  const showSearch = SCOPES_WITH_SEARCH.has(scope);

  const applyFlags = useCallback(
    (items: FeatureFlagItemType[]) => {
      setFlagItems(items);
      setValue('flags', toFlagValues(items));
    },
    [setValue]
  );

  const { apiCaller: fetchAllFlagsRaw, loading: allFlagsLoading } = useOneRequest(apiGetFeatureFlagList);

  const { apiCaller: fetchFlagsByEntityRaw, loading: searchLoading } = useOneRequest(
    apiGetFeatureFlagListWithSearch
  );

  const { apiCaller: saveFlagsRaw, loading: saveLoading } = useOneRequest(apiSaveFeatureFlagList);

  useEffect(() => {
    fetchAllFlagsRaw().then((res) => applyFlags(res.filter((item) => item.scope === scope)));
  }, [fetchAllFlagsRaw, applyFlags, scope]);

  const tableLoading = allFlagsLoading || searchLoading;

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string | null) => {
        const query = (value || '').trim();
        const entityId = parseInt(query, 10);

        if (!query || Number.isNaN(entityId)) {
          setActiveEntityId(undefined);
          fetchAllFlagsRaw().then((res) => applyFlags(res.filter((item) => item.scope === scope)));
        } else {
          setActiveEntityId(entityId);
          fetchFlagsByEntityRaw({ scope, targetID: entityId }).then((res) => applyFlags(res));
        }
      }, 400),
    [fetchAllFlagsRaw, fetchFlagsByEntityRaw, applyFlags, scope]
  );

  const columns: UiTableColumnItemType<FeatureFlagItemType>[] = useMemo(
    () => [
      {
        title: 'Enabled',
        className: 'text-center enabled-column',
        renderValue: ({ id, isEnabled }) => (
          <UiCleanCheckbox
            formProps={formProps}
            label=""
            name={`flags.${id}`}
            defaultChecked={isEnabled}
            wrapperClassname="m-0"
          />
        ),
      },
      {
        title: 'Flag Name',
        className: 'text-left',
        renderValue: ({ id }) => id,
      },
      {
        title: 'Description',
        className: 'text-left',
        renderValue: ({ description }) => description || '-',
      },
      {
        title: 'Created',
        className: 'text-center',
        renderValue: ({ createdAt }) => CDate.formatStringToDate(createdAt, DATE_FORMAT),
      },
      {
        title: 'Updated',
        className: 'text-center',
        renderValue: ({ updatedAt }) => CDate.formatStringToDate(updatedAt, DATE_FORMAT),
      },
    ],
    [formProps]
  );

  const tableData = useMemo(
    () => ({
      loading: tableLoading,
      error: null,
      response: { Items: flagItems, TotalRecordsCount: flagItems.length },
    }),
    [flagItems, tableLoading]
  );

  const scopeLabel = SCOPE_LABELS[scope];

  const handleSave = onSubmit(({ flags }) => {
    saveFlagsRaw({
      Toggles: flagItems.map(({ id, isEnabled }) => ({
        Name: id,
        isEnabled: flags[id] ?? isEnabled,
        scope,
      })),
      Scope: scope,
      targetID: activeEntityId,
    }).then(() => UiToast.success('successfully saved'));
  });

  return (
    <>
      {showSearch && (
        <div className={styles.searchWrapper}>
          <UiCleanInput
            formProps={formProps}
            name="search"
            placeholder={SEARCH_PLACEHOLDERS[scope]}
            onChange={debouncedSearch}
            showAmount={false}
          />
        </div>
      )}

      {showSearch && (
        <div className={styles.warningBanner}>
          {activeEntityId
            ? `Changes will be applied to ${scopeLabel} with ID: ${activeEntityId}`
            : [
                `Changes will affect all ${scopeLabel}s where it have been already added.`,
                `I.e if you enable, all ${scopeLabel.toLowerCase()}s with already added`,
                'toggle will use logic under toggle functionality, not for all existing',
              ].join(' ')}
        </div>
      )}

      {flagItems.length === 0 && !tableLoading ? (
        <div className={styles.emptyState}>
          <p>No flags in this scope</p>
        </div>
      ) : (
        <UiTable
          columns={columns}
          data={tableData}
          getData={noop}
          dummyTable
          doSetSearchParams={false}
          itemsPerPage={flagItems.length}
          notFoundText="No flags in this scope"
          itemKeyProp="id"
          withoutSearchParams
        />
      )}

      <div className={styles.saveWrapper}>
        <UiButton primary loading={saveLoading} onClick={handleSave}>
          Save
        </UiButton>
      </div>
    </>
  );
};

/** Main page with tabs for scopes and React URLs */
export const FeatureFlagPage: FC = () => {
  const reactUrlItems = useMemo(
    () =>
      routes
        .filter(({ path }) =>
          isArray(path) ? true : !path.startsWith('/test') && !['/featuretoggle', '/whatpagesusereact'].includes(path)
        )
        .flatMap(({ path }) => path)
        .map((route) => ({ id: String(route), url: String(route) })),
    []
  );

  const flaggedRoutes = useMemo(
    () => Object.fromEntries(Object.values(FlaggedRoutes).map(({ path, flag }) => [path, flag])),
    []
  );

  const reactUrlsColumns: UiTableColumnItemType<{ id: string; url: string }>[] = useMemo(
    () => [
      { title: 'URL', className: 'text-left', renderValue: ({ url }) => url },
      { title: 'Feature Flag', className: 'text-left', renderValue: ({ url }) => flaggedRoutes[url] || '-' },
    ],
    [flaggedRoutes]
  );

  const reactUrlsTableData = useMemo(
    () => ({
      loading: false,
      error: null,
      response: { Items: reactUrlItems, TotalRecordsCount: reactUrlItems.length },
    }),
    [reactUrlItems]
  );

  const tabItems: UiTabsPropsType['items'] = useMemo(
    () => [
      ...Object.values(FeatureToggleScopeEnum)
        .filter((scope): scope is FeatureToggleScopeEnum => typeof scope === 'number')
        .map((scope) => ({
          id: `scope-${scope}`,
          title: SCOPE_LABELS[scope],
          content: <ScopeContent key={`scope-${scope}`} scope={scope} />,
        })),
      {
        id: 'react-urls',
        title: 'React Pages',
        content: (
          <UiTable
            columns={reactUrlsColumns}
            data={reactUrlsTableData}
            getData={noop}
            dummyTable
            doSetSearchParams={false}
            itemsPerPage={reactUrlItems.length}
            notFoundText="No React pages found"
            itemKeyProp="id"
            withoutSearchParams
          />
        ),
      },
    ],
    [reactUrlsColumns, reactUrlsTableData, reactUrlItems.length]
  );

  return (
    <div className="container my-account-specific-container r-frontend-feature-flags pb-150">
      <div className="account-management-table">
        <h3 className={styles.pageTitle}>Feature flags manage page</h3>
        <UiTabs items={tabItems} contentClassName="account-management-table" />
      </div>
    </div>
  );
};
